<script setup lang="ts">
/* =============================================================
   BERTH — BLOCKED TIME
   =============================================================
   When the berth cannot be booked, and why. A berth is open
   unless something here (or a live booking) overlaps the stay,
   so an empty list means "bookable any time".

   Adding opens a page of its own (BlockFormPage). Add and remove only. A block is two times and a reason; fixing
   one is quicker as remove-and-add than as an edit form.
   ============================================================= */

import { onMounted, ref, useTemplateRef, watch } from 'vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import Badge from '@/components/ui/Badge.vue'
import ItemList from '@/components/ui/ItemList.vue'
import ItemRow from '@/components/ui/ItemRow.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import { deleteBlock, listBlocks } from '@/lib/blocks'
import { blockReasonLabel, formatDateTime, formatDuration } from '@/lib/bookingLabels'
import { to } from '@/config/routes'
import { errorMessage } from '@/lib/errors'
import type { BerthBlock } from '@/lib/types'

const props = defineProps<{ berthId: number }>()

const blocks = ref<BerthBlock[]>([])
const loading = ref(true)
const error = ref('')

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    blocks.value = await listBlocks(props.berthId)
  } catch (e) {
    error.value = errorMessage(e, 'Could not load blocked time.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => props.berthId, load)

const isPast = (b: BerthBlock) => new Date(b.ends_at).getTime() < Date.now()

/* --- remove -------------------------------------------------------------- */

const removing = ref<BerthBlock | null>(null)
const confirm = useTemplateRef<InstanceType<typeof ConfirmDialog>>('confirm')

async function onRemoveConfirmed(): Promise<void> {
  if (!removing.value) return
  try {
    await deleteBlock(removing.value.id)
    removing.value = null
    await load()
  } catch (e) {
    confirm.value?.fail(errorMessage(e, 'Could not remove the block.'))
  }
}
</script>

<template>
  <Panel
    flush
    title="Blocked time"
    description="The berth is open unless a block or a booking overlaps. Times are in your browser's time zone."
  >
    <template #actions>
      <Button size="sm" icon="plus" :to="to.berthBlockNew(berthId)">Block time</Button>
    </template>

    <Alert v-if="error" tone="danger" class="alert">{{ error }}</Alert>

    <p v-if="loading && blocks.length === 0" class="panel-status">Loading…</p>
    <p v-else-if="blocks.length === 0" class="panel-status">Nothing blocked — bookable any time.</p>

    <ItemList v-else>
      <ItemRow v-for="block in blocks" :key="block.id" :muted="isPast(block)">
        <template #title>{{ blockReasonLabel(block.reason) }}</template>
        <template #aside>
          <Badge :tone="isPast(block) ? 'neutral' : 'warning'">
            {{ formatDuration(block.starts_at, block.ends_at) }}
          </Badge>
        </template>
        {{ formatDateTime(block.starts_at) }} → {{ formatDateTime(block.ends_at) }}
        <template #actions>
          <Button size="sm" variant="ghost" icon="trash" icon-only aria-label="Remove block" @click="removing = block" />
        </template>
      </ItemRow>
    </ItemList>
  </Panel>

  <ConfirmDialog
    ref="confirm"
    :open="removing !== null"
    title="Remove block"
    :message="
      removing
        ? `${blockReasonLabel(removing.reason)}, ${formatDateTime(removing.starts_at)} – ${formatDateTime(removing.ends_at)}, will be removed and the time becomes bookable.`
        : ''
    "
    confirm-label="Remove"
    @close="removing = null"
    @confirmed="onRemoveConfirmed"
  />
</template>

<style scoped>
.alert {
  margin: var(--space-sm) var(--space-md) 0;
}
</style>
